import { useMemo, useState } from 'react';
// @mui
import { Grid, Skeleton } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
// components
import NoDataChart from 'src/components/chart/NoDataChart';
import TileWrapper from 'src/components/wrappers/TileWrapper';
import { ChartPlaceholder } from 'src/components/chart/NoDataPlaceholder';

import { getRoundedNumber } from 'src/utils/formatNumber';
import { ImpactReporting } from 'src/@types/reporting';

// ----------------------------------------------------------------------

const chartCommonSetting = {
  height: 300,
  colors: ['#2F795E'],
  margin: { bottom: 65, left: 60, right: 40 },
  slotProps: {
    legend: {
      seriesToDisplay: [ {
        color: '#2F795E',
        label: 'CO₂ amount',
        id: 'co2Kgs'
      } ],
      direction: 'row' as const,
      position: { vertical: 'bottom' as const, horizontal: 'left' as const },
      padding: 0,
      labelStyle: {
        fontSize: 12,
      },
    },
  },
  leftAxis: {
    axisId: 'DEFAULT_Y_AXIS_KEY',
    tickLabelStyle: {
      fontSize: '10px',
    }
  }
};

interface Props {
  dataset: ImpactReporting | null;
  previousImpact: {
    co2Kgs: number | null;
    numberOfContributions: number;
  };
  showLineChart?: boolean;
  showBarChart?: boolean;
}

const ImpactGraphs: React.FC<Props> = ({ dataset, previousImpact, showLineChart, showBarChart }) => {
  const [isDatasetLoading, setIsDatasetLoading] = useState(true);

  const chartData = useMemo(() => {
    let data: any[] = [];

    if (dataset?.rows && dataset?.rows.length > 0) {
      data = dataset?.rows.map((row) => {
        const key = row.dimensionValues[0].value;
        const co2Kgs = +row.metricValues[0].value;
        const numberOfContributions = +row.metricValues[1].value;
        return { key, co2Kgs, numberOfContributions };
      });
    } else {
      data = [{ key: 'no data', co2Kgs: null, numberOfContributions: 0 }];
    }

    return data;
  }, [dataset?.rows]);

  const accDataset = useMemo(() => {

    const data: {co2Kgs: number | null; numberOfContributions: number}[] = JSON.parse(JSON.stringify(chartData));

    if(data.length > 0 && data && data[0]) {
      if(data[0].co2Kgs != null) {
        data[0].co2Kgs += previousImpact.co2Kgs as number;
      } else {
        data[0].co2Kgs = previousImpact.co2Kgs as number;
      }
      data[0].numberOfContributions += previousImpact.numberOfContributions as number;
    }

    data.forEach((record, index) => {
        if(index > 0) {
          if(record.co2Kgs != null) {
            record.co2Kgs += data[index - 1].co2Kgs as number;
          } else {
            record.co2Kgs = data[index - 1].co2Kgs;
          }
          record.numberOfContributions += data[index - 1].numberOfContributions;
        }
    });

    if (chartData?.some((el) => el.co2Kgs)) {
      setIsDatasetLoading(false);
    } else {
      setTimeout(() => {
        setIsDatasetLoading(false); // gracefully set loading to false if there is still empty array after 5 sec
      }, 5000);
    }

    return data;
  }, [chartData, previousImpact]);

  const hasDataset = useMemo(() => chartData?.some((el) => el.co2Kgs), [chartData]);
  const hasAccDataset = useMemo(() => accDataset?.some((el) => el.co2Kgs), [accDataset]);

  const noDataOverTimeText = 'No carbon offsets processed for the selected period. Check in again later or select different dates.';
  const noDataCumulativeText = 'Watch your impact grow here once your programme kicks off.';

  const barChartGraph = (
    <>
      {hasDataset && !isDatasetLoading && (
        <BarChart
          dataset={chartData}
          xAxis={[{ scaleType: 'band', dataKey: 'key' }]}
          series={[
            {
              stack: 'total',
              type: 'bar',
              dataKey: 'co2Kgs',
              label: 'CO₂ amount',
              valueFormatter: (v: number) => `${getRoundedNumber(v)} kg`,
            },
            {
              stack: 'total',
              dataKey: 'numberOfContributions',
              label: 'Number of contributions',
            },
          ]}
          layout="vertical"
          {...chartCommonSetting}
        />
      )}
      {isDatasetLoading && (
        <Skeleton variant="rectangular" sx={{ width: 1, height: 260, borderRadius: 0.5 }} />
      )}
      {!hasDataset && !isDatasetLoading && (
        <NoDataChart text={noDataOverTimeText} Placeholder={ChartPlaceholder} />
      )}
    </>
  );

  const lineChartGraph = (
    <>
      {hasAccDataset && !isDatasetLoading && (
        <LineChart
          dataset={accDataset}
          xAxis={[{ scaleType: 'point', dataKey: 'key' }]}
          series={[
            {
              stack: 'total',
              dataKey: 'co2Kgs',
              label: 'CO₂ amount',
              showMark: (val: any) => val !== null,
              area: true,
              valueFormatter: (v: number) => `${getRoundedNumber(v)} kg`,
            },
            {
              stack: 'total',
              dataKey: 'numberOfContributions',
              label: 'Number of contributions',
              showMark: false,
            },
          ]}
          {...chartCommonSetting}
        />
      )}
      {isDatasetLoading && (
        <Skeleton variant="rectangular" sx={{ width: 1, height: 260, borderRadius: 0.5 }} />
      )}
      {!hasAccDataset && !isDatasetLoading && (
        <NoDataChart text={noDataCumulativeText} Placeholder={ChartPlaceholder} />
      )}
    </>
  );

  if (showLineChart) {
    return lineChartGraph;
  }

  if (showBarChart) {
    return barChartGraph;
  }

  return (
    <>
      <Grid item xs={12} md={6}>
        <TileWrapper
          title="Carbon offset over time"
          sx={{ minHeight: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          {barChartGraph}
        </TileWrapper>
      </Grid>

      <Grid item xs={12} md={6}>
        <TileWrapper
          title="Cumulative impact"
          sx={{ minHeight: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          {lineChartGraph}
        </TileWrapper>
      </Grid>
    </>
  );
};

export default ImpactGraphs;
